import { attachMenuSounds } from './MenuSounds';

export type DevView = 'game' | 'map' | 'rig' | 'assets' | 'sfx';
export type DevDockStatusKind = 'ok' | 'info' | 'error';

export interface DevDock {
  readonly root: HTMLElement;
  setContent(node: HTMLElement | null): void;
  setCollapsed(collapsed: boolean): void;
  setStatus(message: string, kind?: DevDockStatusKind): void;
  flashSaved(label?: string): void;
}

export interface MountDevDockOptions {
  initialView: DevView;
}

const COLLAPSED_KEY = 'tst:dev-dock:collapsed';
const WIDTH_KEY = 'tst:dev-dock:width';
const DEFAULT_WIDTH = 520;
const MIN_WIDTH = 340;
const MAX_MARGIN = 24;

const TABS: ReadonlyArray<{ view: DevView; label: string; href: string }> = [
  { view: 'game', label: 'Game', href: '/?dev' },
  { view: 'map', label: 'Map', href: '/?devView' },
  { view: 'rig', label: 'Rig', href: '/?rigReview' },
  { view: 'assets', label: 'Assets', href: '/?assetView' },
  { view: 'sfx', label: 'SFX', href: '/audio-tuner.html' },
];

export function mountDevDock(parent: HTMLElement, opts: MountDevDockOptions): DevDock {
  const root = document.createElement('aside');
  root.id = 'dev-dock';
  root.dataset.activeView = opts.initialView;
  root.setAttribute('aria-label', 'Debug dock');

  const header = document.createElement('div');
  header.className = 'dev-dock-header';

  const tabs = document.createElement('div');
  tabs.className = 'dev-dock-tabs';
  tabs.setAttribute('role', 'tablist');
  for (const { view, label, href } of TABS) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'tst-btn tst-btn-sm dev-dock-tab';
    button.dataset.view = view;
    if (view === opts.initialView) {
      button.classList.add('is-active');
      button.setAttribute('aria-selected', 'true');
    } else {
      button.setAttribute('aria-selected', 'false');
    }
    button.setAttribute('role', 'tab');
    button.textContent = label;
    button.addEventListener('click', () => {
      if (view === opts.initialView) return; // already here
      window.location.assign(preserveLandscape(href));
    });
    tabs.appendChild(button);
  }

  const collapseBtn = document.createElement('button');
  collapseBtn.type = 'button';
  collapseBtn.className = 'dev-dock-collapse';
  collapseBtn.setAttribute('aria-label', 'Toggle dev dock');
  collapseBtn.title = 'Collapse dock (`)';

  const resizeHandle = document.createElement('div');
  resizeHandle.className = 'dev-dock-resize';
  resizeHandle.setAttribute('role', 'separator');
  resizeHandle.setAttribute('aria-orientation', 'vertical');
  resizeHandle.setAttribute('aria-label', 'Resize dev dock');
  resizeHandle.title = 'Drag to resize';

  const slot = document.createElement('div');
  slot.className = 'dev-dock-slot';

  const status = document.createElement('div');
  status.className = 'dev-dock-status is-empty';
  status.dataset.kind = 'info';
  status.textContent = '';

  root.appendChild(resizeHandle);
  header.appendChild(tabs);
  header.appendChild(collapseBtn);
  root.appendChild(header);
  root.appendChild(slot);
  root.appendChild(status);

  let collapsed = readCollapsed();
  let width = readWidth();
  applyWidth(root, width);
  applyCollapsed(root, collapseBtn, collapsed);

  collapseBtn.addEventListener('click', () => {
    collapsed = !collapsed;
    applyCollapsed(root, collapseBtn, collapsed);
    writeCollapsed(collapsed);
  });

  const onKey = (event: KeyboardEvent): void => {
    if (event.key !== '`') return;
    const target = event.target as HTMLElement | null;
    if (target && target.matches('input, textarea, select, [contenteditable="true"]')) return;
    collapsed = !collapsed;
    applyCollapsed(root, collapseBtn, collapsed);
    writeCollapsed(collapsed);
  };
  window.addEventListener('keydown', onKey);

  let resizing = false;
  let resizePointerId = -1;
  const onResizePointerMove = (event: PointerEvent): void => {
    if (!resizing || event.pointerId !== resizePointerId) return;
    const nextWidth = clampWidth(window.innerWidth - event.clientX);
    width = nextWidth;
    applyWidth(root, width);
  };
  const finishResize = (event: PointerEvent): void => {
    if (!resizing || event.pointerId !== resizePointerId) return;
    resizing = false;
    resizePointerId = -1;
    root.classList.remove('is-resizing');
    writeWidth(width);
    try { resizeHandle.releasePointerCapture(event.pointerId); } catch { /* ignore */ }
    window.removeEventListener('pointermove', onResizePointerMove);
    window.removeEventListener('pointerup', finishResize);
    window.removeEventListener('pointercancel', finishResize);
  };
  resizeHandle.addEventListener('pointerdown', (event) => {
    if (collapsed) return;
    resizing = true;
    resizePointerId = event.pointerId;
    root.classList.add('is-resizing');
    resizeHandle.setPointerCapture(event.pointerId);
    window.addEventListener('pointermove', onResizePointerMove);
    window.addEventListener('pointerup', finishResize);
    window.addEventListener('pointercancel', finishResize);
    event.preventDefault();
  });
  resizeHandle.addEventListener('dblclick', () => {
    width = DEFAULT_WIDTH;
    applyWidth(root, width);
    writeWidth(width);
  });

  attachMenuSounds(root);
  parent.appendChild(root);
  document.body.classList.add('dev-dock-mounted');

  let flashTimer: number | null = null;

  const dock: DevDock = {
    root,
    setContent(node) {
      slot.replaceChildren();
      if (node) slot.appendChild(node);
      root.classList.toggle('has-content', Boolean(node));
    },
    setCollapsed(next) {
      collapsed = next;
      applyCollapsed(root, collapseBtn, collapsed);
      writeCollapsed(collapsed);
    },
    setStatus(message, kind = 'info') {
      status.textContent = message;
      status.dataset.kind = kind;
      status.classList.toggle('is-empty', message.length === 0);
    },
    flashSaved(label = 'Saved') {
      const time = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', second: '2-digit' });
      this.setStatus(`✓ ${label} ${time}`, 'ok');
      if (flashTimer !== null) window.clearTimeout(flashTimer);
      flashTimer = window.setTimeout(() => {
        if (status.dataset.kind === 'ok') this.setStatus('', 'info');
        flashTimer = null;
      }, 4000);
    },
  };

  return dock;
}

function applyWidth(root: HTMLElement, width: number): void {
  root.style.setProperty('--dev-dock-width', `${clampWidth(width)}px`);
}

function applyCollapsed(root: HTMLElement, button: HTMLButtonElement, collapsed: boolean): void {
  root.classList.toggle('is-collapsed', collapsed);
  button.textContent = collapsed ? '‹' : '›';
  button.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
}

function readWidth(): number {
  try {
    const parsed = Number(window.localStorage.getItem(WIDTH_KEY));
    return Number.isFinite(parsed) ? clampWidth(parsed) : DEFAULT_WIDTH;
  } catch {
    return DEFAULT_WIDTH;
  }
}

function writeWidth(width: number): void {
  try {
    window.localStorage.setItem(WIDTH_KEY, String(clampWidth(width)));
  } catch {
    /* ignore quota / private mode */
  }
}

function clampWidth(width: number): number {
  const max = Math.max(MIN_WIDTH, window.innerWidth - MAX_MARGIN);
  return Math.round(Math.max(MIN_WIDTH, Math.min(max, width)));
}

function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function writeCollapsed(collapsed: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
}

function preserveLandscape(href: string): string {
  try {
    const params = new URLSearchParams(window.location.search);
    const landscape = params.get('landscape');
    if (!landscape) return href;
    const sep = href.includes('?') ? '&' : '?';
    return `${href}${sep}landscape=${encodeURIComponent(landscape)}`;
  } catch {
    return href;
  }
}
